import { useState, useReducer, useEffect, useContext } from 'react';
import { useForm, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import { isEmployeeResponsable, getCurrentUserStaffID } from '../../utils/staff';

/**
 * Contexts
 */
import { AppContext } from '../../contexts/AppContext';
import { SharedFormContext } from '../../contexts/SharedFormContext';
import { ReportFormContext } from '../../contexts/ReportFormContext';

/**
 * Hooks
 */
import { useTranslation } from 'react-i18next';

/**
 * Models
 */
import { Consumption } from '../../models/WorkReportSubmodels';

/**
 * MUI Components
 */
import { Button, Box, TextField, Typography, TextareaAutosize } from '@mui/material';

/**
 * Components
 */
import ConsumablesSelect from './Fields/ConsumablesSelect';
import StaffSelectControl from './Fields/StaffSelectControl';
import WorkActionAreaControl from './Fields/WorkActionAreaControl';
import WorkActionSubareaControl from './Fields/WorkActionSubareaControl';
import WorkServiceControl from './Fields/WorkServiceControl';

const styles = {
    formBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '300px',
        margin: '0 auto'
    }
};

// Local initial state
const initialState = {
    notes: '',
    quantity: 0,
    selectStaff: 0,
    selectService: 0,
    selectActionArea: 0,
    selectActionSubarea: 0,
    filteredStaff: [],
    filteredService: [],
    filteredActionArea: [],
    filteredActionSubarea: [],
    isFormValid: false
};

// Local state reducer
const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_FORM_DATA':
            if (action.name === 'all') {
                return { ...state, ...action.value };
            }
            return { ...state, [action.name]: action.value };
        case 'SET_FILTERED_STAFF':
            return {
                ...state,
                filteredStaff: action.filteredStaff,
            };
        case 'SET_FILTERED_SERVICE':
            return {
                ...state,
                filteredService: action.filteredService,
            };
        case 'SET_FILTERED_ACTION_AREA':
            return {
                ...state,
                filteredActionArea: action.filteredActionArea,
            };
        case 'SET_FILTERED_ACTION_SUBAREA':
            return {
                ...state,
                filteredActionSubarea: action.filteredActionSubarea,
            };
        case 'SET_FORM_VALIDITY':
            return {
                ...state,
                isFormValid: action.isFormValid
            };
        default:
            return state;
    }
};

const AddConsumptionForm = ({ onSubmit, planning, oldData }) => {
    const { t } = useTranslation();
    const { control, getValues, reset, watch } = useForm({
        mode: 'onChange', // Set validation mode
    });
    const [selectedConsumable, setSelectedConsumable] = useState(null);
    const appData = useContext(AppContext);
    const { report, setReport } = useContext(ReportFormContext);
    const { updateSharedValue, getSharedValue } = useContext(SharedFormContext);
    const [state, dispatch] = useReducer(reducer, initialState);
    const sid = getCurrentUserStaffID(appData);
    const uid = appData.userInfo?.id || null;
    const isResponsable = isEmployeeResponsable(sid, planning);
    const isEdit = !!oldData;

    const handleSelect = (consumable) => {
        // console.log('Selected Consumable:', consumable);
        setSelectedConsumable(consumable);
        reset();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = getValues();
        // console.log("Data from consumption form:", formData);

        // Guard: prevent invalid submissions
        const q = Number(formData.quantity);
        if (!selectedConsumable || Number.isNaN(q) || q <= 0) {
            console.warn('Cannot submit consumption: invalid inputs', { selectedConsumable, quantity: formData.quantity });
            return;
        }

        const consumption = Consumption.new();
        consumption.sid = state.selectStaff;
        consumption.consumable = selectedConsumable;
        consumption.quantity = formData.quantity;
        consumption.notes = formData.notes;
        consumption.owner_id = uid || 0;
        consumption.workActionAreaId = state.selectActionArea;
        consumption.workActionSubareaId = state.selectActionSubarea;
        consumption.workServiceId = state.selectService;

        if (isEdit) {
            // Modify existing consumption record!
            let index = report.consumptions.indexOf(oldData);
            report.consumptions[index] = consumption;
            setReport({ ...report });
            console.log('Consumption record updated with index', index);
        } else {
            // Add new consumption record!
            report.consumptions.push(consumption);
            setReport({ ...report });

            // Update shared data between forms
            updateSharedValue('selectStaff', state.selectStaff);
            updateSharedValue('selectService', state.selectService);
            updateSharedValue('selectActionArea', state.selectActionArea);
            updateSharedValue('selectActionSubarea', state.selectActionSubarea);

            console.log('New consumption record added');
        }

        // Call onSubmit
        onSubmit();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        dispatch({ type: 'SET_FORM_DATA', name, value });
    };

    // Validate form: must have selected consumable and quantity > 0
    const quantityWatched = watch('quantity');
    useEffect(() => {
        const q = Number(quantityWatched);
        const valid = !!selectedConsumable && Number.isFinite(q) && q > 0;
        dispatch({ type: 'SET_FORM_VALIDITY', isFormValid: valid });
    }, [selectedConsumable, quantityWatched]);

    // Autoset initial state from shared data if exists
    useEffect(() => {
        if (isEdit) {
            // Initialize with old data
            // console.debug('Loading data for edit...', oldData);

            setSelectedConsumable(oldData.consumable);
            reset({
                quantity: oldData.quantity,
                notes: oldData.notes
            });

            // Unify data properties from oldData
            let sid = oldData.staff_id || oldData.sid;
            let workServiceId = oldData.workServiceId || oldData.catalog_servicies?.[0]?.id || 0;
            let workActionAreaId = oldData.workActionAreaId || oldData.work_action_areas?.[0]?.id || 0;
            let workActionSubareaId = oldData.workActionSubareaId || oldData.work_action_subareas?.[0]?.id || 0;

            // Update local state using individual dispatches with correct property names
            dispatch({ type: 'SET_FORM_DATA', name: 'selectStaff', value: sid });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectService', value: workServiceId });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectActionArea', value: workActionAreaId });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: workActionSubareaId });
        } else {
            // Initialize with shared data
            if (getSharedValue('selectStaff')) {
                dispatch({ type: 'SET_FORM_DATA', name: 'selectStaff', value: getSharedValue('selectStaff') });
            }
            if (getSharedValue('selectService')) {
                dispatch({ type: 'SET_FORM_DATA', name: 'selectService', value: getSharedValue('selectService') });
            }
            if (getSharedValue('selectActionArea')) {
                dispatch({ type: 'SET_FORM_DATA', name: 'selectActionArea', value: getSharedValue('selectActionArea') });
            }
            if (getSharedValue('selectActionSubarea')) {
                dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: getSharedValue('selectActionSubarea') });
            }
        }
    }, []);

    /**
     * Staff (Select)
     */
    useEffect(() => {
        let staff = planning?.staff || [];
        // console.log("PLANNING FROM ADD EMPLOYEE HOURS FORM", state, staff);
        if (!isResponsable) {
            // Filter staff to only include the current user if not responsible
            staff = staff.filter((employee) => employee.id === sid);
            console.info('Employee with ID', sid, 'is not responsible.');
        } else {
            console.info('Employee with ID', sid, 'is responsible for', staff);
        }
        dispatch({ type: 'SET_FILTERED_STAFF', filteredStaff: staff });
    }, [planning, isResponsable, sid]);

    /**
     * Work action areas & Work services (Selects)
     */
    useEffect(() => {
        let areas = planning.work?.work_action_areas || [];
        dispatch({ type: 'SET_FILTERED_ACTION_AREA', filteredActionArea: areas });

        let subareas = planning.work?.work_action_subareas || [];
        // console.debug('Subareas loaded', subareas);
        dispatch({ type: 'SET_FILTERED_ACTION_SUBAREA', filteredActionSubarea: subareas });

        let services = planning.work?.catalog_services || [];
        dispatch({ type: 'SET_FILTERED_SERVICE', filteredService: services });
    }, [planning]);

    /**
     * Filter subareas based on current action area selected
     */
    useEffect(() => {
        if (planning?.work?.work_action_subareas) {
            const allSubareas = planning.work.work_action_subareas;
            const filtered = allSubareas.filter(
                (subarea) => subarea.in_action_areas_ids?.includes(state.selectActionArea)
            );
            dispatch({ type: 'SET_FILTERED_ACTION_SUBAREA', filteredActionSubarea: filtered });

            // Check if current subarea is valid for the new zone
            const currentSubareaValid = filtered.some(subarea => subarea.id === state.selectActionSubarea);

            if (filtered.length === 0) {
                // No subareas available for this zone
                if (state.selectActionSubarea !== 0) {
                    dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: 0 });
                    updateSharedValue('selectActionSubarea', 0);
                }
            } else if (!currentSubareaValid && state.selectActionSubarea !== 0) {
                // Current subarea is not valid for the new zone, reset it
                dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: 0 });
                updateSharedValue('selectActionSubarea', 0);
            } else if (currentSubareaValid && state.selectActionSubarea === 0) {
                // No subarea selected but there are valid options, auto-select the first one
                const firstSubarea = filtered[0];
                if (firstSubarea) {
                    dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: firstSubarea.id });
                    updateSharedValue('selectActionSubarea', firstSubarea.id);
                }
            }
            // If current subarea is valid, keep it (no action needed)
        }
    }, [state.selectActionArea, planning]);

    return (
        <Box component="form" onSubmit={handleSubmit} sx={styles.formBox}>

            {/* BEGIN Shared common fields */}
            <StaffSelectControl value={getSharedValue('selectStaff', state.selectStaff || 0)} list={state.filteredStaff} handleChange={handleChange} responsableSID={planning?.responsable?.id} />
            <WorkActionAreaControl value={getSharedValue('selectActionArea', state.selectActionArea || 0)} list={state.filteredActionArea} handleChange={handleChange} showParent={false} hideSectors={true} />
            <WorkActionSubareaControl value={getSharedValue('selectActionSubarea', state.selectActionSubarea || 0)} list={state.filteredActionSubarea} handleChange={handleChange} />
            <WorkServiceControl value={getSharedValue('selectService', state.selectService || 0)} list={state.filteredService} handleChange={handleChange} />
            {/* END Shared common fields */}

            <ConsumablesSelect onSelect={handleSelect} value={oldData?.consumable?.id} />
            {selectedConsumable && (
                <Box mt={2}>
                    <Typography variant="body1">{t('Selected Consumable Details')}:</Typography>
                    {/* <Typography variant="body2">{t('Supplier')}: {selectedConsumable.supplier?.name || t('unknown')}</Typography> */}
                    <Typography variant="body2">{t('Name')}: {selectedConsumable.name}</Typography>
                    <Typography variant="body2">{t('Description')}: {selectedConsumable.description}</Typography>

                    <Controller
                        name="quantity"
                        control={control}
                        rules={{
                            required: t('Quantity is required'),
                            min: { value: 0.000001, message: t('Quantity must be greater than zero') }
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                id="quantity"
                                label={t('Quantity')}
                                type="number"
                                inputProps={{ min: 0.000001, step: 'any' }}
                                required
                                fullWidth
                                sx={{ mt: 2 }}
                                {...field}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />

                    <Controller
                        name="notes"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextareaAutosize
                                id="notes"
                                minRows={3}
                                placeholder={t('Notes')}
                                fullWidth
                                style={{ width: '100%', marginTop: '16px', padding: '10px' }}
                                {...field}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value)}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                </Box>
            )}

            <Button disabled={!state.isFormValid} variant="contained" color="primary" type="submit">
                {isEdit ? t('Update') : t('Add')}
            </Button>
        </Box>
    );
};

AddConsumptionForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    planning: PropTypes.object.isRequired,
    oldData: PropTypes.object,
};

export default AddConsumptionForm;